// src/createControllableSignal.ts
import { access, accessWith } from "@solid-primitives/utils";
import { createMemo, createSignal, untrack } from "solid-js";
function createControllableSignal(props) {
  const [_value, _setValue] = createSignal(access(props.defaultValue));
  const isControlled = createMemo(() => access(props.value) !== void 0);
  const value = createMemo(() => isControlled() ? access(props.value) : _value());
  const setValue = (next) => {
    untrack(() => {
      var _a;
      const nextValue = accessWith(next, value());
      if (!Object.is(nextValue, value())) {
        if (!isControlled()) {
          _setValue(nextValue);
        }
        (_a = props.onChange) == null ? void 0 : _a.call(props, nextValue);
      }
      return nextValue;
    });
  };
  return [value, setValue];
}
function createControllableBooleanSignal(props) {
  const [_value, setValue] = createControllableSignal(props);
  const value = () => _value() ?? false;
  return [value, setValue];
}
function createControllableArraySignal(props) {
  const [_value, setValue] = createControllableSignal(props);
  const value = () => _value() ?? [];
  return [value, setValue];
}
function createControllableSetSignal(props) {
  const [_value, setValue] = createControllableSignal(props);
  const value = () => _value() ?? /* @__PURE__ */ new Set();
  return [value, setValue];
}

// src/createDescription.ts
import { isServer } from "@solid-primitives/utils";
import { createEffect, createSignal as createSignal2, on, onCleanup } from "solid-js";
var descriptionId = 0;
var descriptionNodes = /* @__PURE__ */ new Map();
function createDescription(description) {
  const [id, setId] = createSignal2();
  createEffect(on(description, (newDescription) => {
    if (!newDescription || isServer) {
      return;
    }
    let desc = descriptionNodes.get(newDescription);
    if (!desc) {
      const id2 = `solid-aria-description-${descriptionId++}`;
      setId(id2);
      const node = document.createElement("div");
      node.id = id2;
      node.style.display = "none";
      node.textContent = newDescription;
      document.body.appendChild(node);
      desc = { refCount: 0, element: node };
      descriptionNodes.set(newDescription, desc);
    } else {
      setId(desc.element.id);
    }
    desc.refCount++;
    onCleanup(() => {
      if (desc && --desc.refCount === 0) {
        desc.element.remove();
        descriptionNodes.delete(newDescription);
      }
    });
  }));
  const descriptionProps = {
    get "aria-describedby"() {
      return description() ? id() : void 0;
    }
  };
  return descriptionProps;
}

// src/createGlobalListeners.ts
import { onCleanup as onCleanup2 } from "solid-js";
function createGlobalListeners() {
  const globalListeners = /* @__PURE__ */ new Map();
  const addGlobalListener = (eventTarget, type, listener, options) => {
    const fn = (options == null ? void 0 : options.once) ? (...args) => {
      globalListeners.delete(listener);
      listener(...args);
    } : listener;
    globalListeners.set(listener, { type, eventTarget, fn, options });
    eventTarget.addEventListener(type, listener, options);
  };
  const removeGlobalListener = (eventTarget, type, listener, options) => {
    var _a;
    const fn = ((_a = globalListeners.get(listener)) == null ? void 0 : _a.fn) || listener;
    eventTarget.removeEventListener(type, fn, options);
    globalListeners.delete(listener);
  };
  const removeAllGlobalListeners = () => {
    globalListeners.forEach((value, key) => {
      removeGlobalListener(value.eventTarget, value.type, key, value.options);
    });
  };
  onCleanup2(() => {
    removeAllGlobalListeners();
  });
  return { addGlobalListener, removeGlobalListener, removeAllGlobalListeners };
}

// src/createId.ts
import { isServer as isServer2, noop } from "@solid-primitives/utils";
import { createSignal as createSignal3, createUniqueId, getListener, onCleanup as onCleanup3 } from "solid-js";
var ID_PREFIX = "solid-aria";
function createId(prefix = ID_PREFIX) {
  return `${prefix}-${createUniqueId()}`;
}
function createSlotId(prefix) {
  const id = createId(prefix);
  if (isServer2)
    return [() => id, noop];
  const [slotId, setSlotId] = createSignal3(id);
  const updateSlotId = () => setSlotId(document.getElementById(id) ? id : void 0);
  queueMicrotask(updateSlotId);
  const track = () => {
    queueMicrotask(updateSlotId);
    getListener() && onCleanup3(updateSlotId);
  };
  return [slotId, track];
}

// src/createSyncRef.ts
import { createEffect as createEffect2, onCleanup as onCleanup4 } from "solid-js";
function createSyncRef(context, ref) {
  createEffect2(() => {
    context.ref = ref;
    onCleanup4(() => {
      context.ref = () => void 0;
    });
  });
}

// src/filterDOMProps.ts
import { splitProps } from "solid-js";
var DOMPropNames = /* @__PURE__ */ new Set(["id"]);
var labelablePropNames = /* @__PURE__ */ new Set([
  "aria-label",
  "aria-labelledby",
  "aria-describedby",
  "aria-details"
]);
var dataAttrsRegex = /^(data-.*)$/;
function filterDOMProps(props, opts = {}) {
  const { labelable, propNames } = opts;
  const filteredPropNames = [];
  for (const prop in props) {
    if (Object.prototype.hasOwnProperty.call(props, prop) && (DOMPropNames.has(prop) || labelable && labelablePropNames.has(prop) || (propNames == null ? void 0 : propNames.has(prop)) || dataAttrsRegex.test(prop))) {
      filteredPropNames.push(prop);
    }
  }
  const [filteredProps] = splitProps(props, filteredPropNames);
  return filteredProps;
}

// src/focusWithoutScrolling.ts
function focusWithoutScrolling(element) {
  if (supportsPreventScroll()) {
    element.focus({ preventScroll: true });
  } else {
    const scrollableElements = getScrollableElements(element);
    element.focus();
    restoreScrollPosition(scrollableElements);
  }
}
var supportsPreventScrollCached = null;
function supportsPreventScroll() {
  if (supportsPreventScrollCached == null) {
    supportsPreventScrollCached = false;
    try {
      const focusElem = document.createElement("div");
      focusElem.focus({
        get preventScroll() {
          supportsPreventScrollCached = true;
          return true;
        }
      });
    } catch (e) {
    }
  }
  return supportsPreventScrollCached;
}
function getScrollableElements(element) {
  let parent = element.parentNode;
  const scrollableElements = [];
  const rootScrollingElement = document.scrollingElement || document.documentElement;
  while (parent instanceof HTMLElement && parent !== rootScrollingElement) {
    if (parent.offsetHeight < parent.scrollHeight || parent.offsetWidth < parent.scrollWidth) {
      scrollableElements.push({
        element: parent,
        scrollTop: parent.scrollTop,
        scrollLeft: parent.scrollLeft
      });
    }
    parent = parent.parentNode;
  }
  if (rootScrollingElement instanceof HTMLElement) {
    scrollableElements.push({
      element: rootScrollingElement,
      scrollTop: rootScrollingElement.scrollTop,
      scrollLeft: rootScrollingElement.scrollLeft
    });
  }
  return scrollableElements;
}
function restoreScrollPosition(scrollableElements) {
  for (const { element, scrollTop, scrollLeft } of scrollableElements) {
    element.scrollTop = scrollTop;
    element.scrollLeft = scrollLeft;
  }
}

// src/getScrollParent.ts
function getScrollParent(node) {
  while (node && !isScrollable(node)) {
    node = node.parentElement;
  }
  return node || document.scrollingElement || document.documentElement;
}
function isScrollable(node) {
  const style = window.getComputedStyle(node);
  return /(auto|scroll)/.test(style.overflow + style.overflowX + style.overflowY);
}

// src/handler.ts
function callHandler(handler, event) {
  if (handler) {
    if (typeof handler === "function") {
      handler(event);
    } else {
      handler[0](handler[1], event);
    }
  }
  return event == null ? void 0 : event.defaultPrevented;
}

// src/mergeAriaLabels.ts
import { access as access2 } from "@solid-primitives/utils";
function mergeAriaLabels(props, defaultAriaLabel) {
  const defaultId = createId();
  const id = () => access2(props.id) ?? defaultId;
  const ariaLabelledby = () => {
    const label = access2(props["aria-label"]);
    let labelledBy = access2(props["aria-labelledby"]);
    if (labelledBy && label) {
      const ids = /* @__PURE__ */ new Set([...labelledBy.trim().split(/\s+/), id()]);
      labelledBy = [...ids].join(" ");
    } else if (labelledBy) {
      labelledBy = labelledBy.trim().split(/\s+/).join(" ");
    }
    return labelledBy;
  };
  const ariaLabel = () => {
    const defaultLabel = access2(defaultAriaLabel);
    let label = access2(props["aria-label"]);
    if (!label && !ariaLabelledby() && defaultLabel) {
      label = defaultLabel;
    }
    return label;
  };
  const ariaLabelsProps = {
    get id() {
      return id();
    },
    get "aria-labelledby"() {
      return ariaLabelledby();
    },
    get "aria-label"() {
      return ariaLabel();
    }
  };
  return { ariaLabelsProps };
}

// src/number.ts
function clamp(value, min = -Infinity, max = Infinity) {
  const newValue = Math.min(Math.max(value, min), max);
  return newValue;
}
function snapValueToStep(value, min, max, step) {
  const remainder = (value - (min == null || isNaN(min) ? 0 : min)) % step;
  let snappedValue = Math.abs(remainder) * 2 >= step ? value + Math.sign(remainder) * (step - Math.abs(remainder)) : value - remainder;
  if (min != null && !isNaN(min)) {
    if (snappedValue < min) {
      snappedValue = min;
    } else if (max != null && !isNaN(max) && snappedValue > max) {
      snappedValue = min + Math.floor((max - min) / step) * step;
    }
  } else if (max != null && !isNaN(max) && snappedValue > max) {
    snappedValue = Math.floor(max / step) * step;
  }
  const string = step.toString();
  const index = string.indexOf(".");
  const precision = index >= 0 ? string.length - index : 0;
  if (precision > 0) {
    const pow = Math.pow(10, precision);
    snappedValue = Math.round(snappedValue * pow) / pow;
  }
  return snappedValue;
}
function toFixedNumber(value, digits, base = 10) {
  const pow = Math.pow(base, digits);
  return Math.round(value * pow) / pow;
}

// src/runAfterTransition.ts
var transitionsByElement = /* @__PURE__ */ new Map();
var transitionCallbacks = /* @__PURE__ */ new Set();
function setupGlobalEvents() {
  if (typeof window === "undefined") {
    return;
  }
  const onTransitionStart = (e) => {
    var _a;
    if (!e.target) {
      return;
    }
    let transitions = transitionsByElement.get(e.target);
    if (!transitions) {
      transitions = /* @__PURE__ */ new Set();
      transitionsByElement.set(e.target, transitions);
      (_a = e.target) == null ? void 0 : _a.addEventListener("transitioncancel", onTransitionEnd);
    }
    transitions.add(e.propertyName);
  };
  const onTransitionEnd = (e) => {
    var _a;
    if (!e.target) {
      return;
    }
    const properties = transitionsByElement.get(e.target);
    if (!properties) {
      return;
    }
    properties.delete(e.propertyName);
    if (properties.size === 0) {
      (_a = e.target) == null ? void 0 : _a.removeEventListener("transitioncancel", onTransitionEnd);
      transitionsByElement.delete(e.target);
    }
    if (transitionsByElement.size === 0) {
      for (const cb of transitionCallbacks) {
        cb();
      }
      transitionCallbacks.clear();
    }
  };
  document.body.addEventListener("transitionrun", onTransitionStart);
  document.body.addEventListener("transitionend", onTransitionEnd);
}
if (typeof document !== "undefined") {
  if (document.readyState !== "loading") {
    setupGlobalEvents();
  } else {
    document.addEventListener("DOMContentLoaded", setupGlobalEvents);
  }
}
function runAfterTransition(fn) {
  requestAnimationFrame(() => {
    if (transitionsByElement.size === 0) {
      fn();
    } else {
      transitionCallbacks.add(fn);
    }
  });
}

// src/scrollIntoView.ts
function scrollIntoView(scrollView, element) {
  const offsetX = relativeOffset(scrollView, element, "left");
  const offsetY = relativeOffset(scrollView, element, "top");
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  let x = scrollView.scrollLeft;
  let y = scrollView.scrollTop;
  const maxX = x + scrollView.offsetWidth;
  const maxY = y + scrollView.offsetHeight;
  if (offsetX <= x) {
    x = offsetX;
  } else if (offsetX + width > maxX) {
    x += offsetX + width - maxX;
  }
  if (offsetY <= y) {
    y = offsetY;
  } else if (offsetY + height > maxY) {
    y += offsetY + height - maxY;
  }
  scrollView.scrollLeft = x;
  scrollView.scrollTop = y;
}
function relativeOffset(ancestor, child, axis) {
  const prop = axis === "left" ? "offsetLeft" : "offsetTop";
  let sum = 0;
  while (child.offsetParent) {
    sum += child[prop];
    if (child.offsetParent === ancestor) {
      break;
    } else if (child.offsetParent.contains(ancestor)) {
      sum -= ancestor[prop];
      break;
    }
    child = child.offsetParent;
  }
  return sum;
}
export {
  ID_PREFIX,
  callHandler,
  clamp,
  createControllableArraySignal,
  createControllableBooleanSignal,
  createControllableSetSignal,
  createControllableSignal,
  createDescription,
  createGlobalListeners,
  createId,
  createSlotId,
  createSyncRef,
  filterDOMProps,
  focusWithoutScrolling,
  getScrollParent,
  mergeAriaLabels,
  runAfterTransition,
  scrollIntoView,
  snapValueToStep,
  toFixedNumber
};
